import { useEffect, useMemo, useState } from 'react';
import { cn } from '@shared/lib';
import type { AnswerPhase, SessionCard } from '../../model';
import { DISTRACTORS } from '../../model';

interface McqOptionsProps {
  card: SessionCard;
  phase: AnswerPhase;
  gaveUp: boolean;
  onAnswer: (userInput: string) => void;
  /** Десктоп подписывает варианты цифрами 1–4 и слушает их на клавиатуре. */
  withDigits: boolean;
}

/** Четыре варианта перевода. Верный — card.ru, дистракторы приходят с бэка
 *  (подобраны так, чтобы грейдер их не засчитал). Порядок тасуется один раз
 *  на карточку, поэтому варианты не прыгают при перерисовках. */
export function McqOptions({ card, phase, gaveUp, onAnswer, withDigits }: McqOptionsProps) {
  const opts = useMemo(() => {
    const lower = card.ru.toLowerCase();
    const pool = (card.selectionOptions ?? DISTRACTORS).filter(
      (d) => d.toLowerCase() !== lower,
    );
    const wrong = pool
      .slice()
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);
    return [card.ru, ...wrong].sort(() => Math.random() - 0.5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id, card.ru, card.selectionOptions]);
  const correctIdx = opts.indexOf(card.ru);
  const [picked, setPicked] = useState<number | null>(null);

  // Сброс выбора на смене карточки И при любом возврате в answering (повтор той
  // же карточки — itemId не меняется; либо ошибка отправки submitting→answering).
  useEffect(() => setPicked(null), [card.id]);
  useEffect(() => {
    if (phase === 'answering') setPicked(null);
  }, [phase]);

  const click = (i: number) => {
    if (phase !== 'answering') return;
    setPicked(i);
    onAnswer(opts[i]);
  };

  useEffect(() => {
    if (!withDigits || phase !== 'answering') return;
    const handler = (e: KeyboardEvent) => {
      const n = parseInt(e.key, 10);
      if (Number.isFinite(n) && n >= 1 && n <= opts.length) {
        e.preventDefault();
        click(n - 1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, opts, withDigits]);

  const answered = phase === 'feedback';

  return (
    <div className="st-mcq">
      {opts.map((o, i) => {
        const isCorrect = i === correctIdx;
        const isPicked = picked === i;
        // Промах: свой вариант красным, верный — пунктирным контуром. «не помню»
        // выбора не делал, поэтому просто раскрываем верный.
        const cls = isPicked
          ? isCorrect
            ? 'is-correct'
            : 'is-wrong'
          : answered && isCorrect && (picked !== null || gaveUp)
            ? 'is-reveal'
            : answered
              ? 'is-dim'
              : '';
        return (
          <button
            key={i}
            type="button"
            className={cn('st-opt', cls)}
            onClick={() => click(i)}
            disabled={phase !== 'answering'}
          >
            {withDigits && <span className="st-opt-key">{i + 1}</span>}
            <span className="st-opt-text">{o.toLowerCase()}</span>
            {(isPicked || (answered && isCorrect)) && (
              <span className="st-opt-glyph" aria-hidden>
                {isCorrect ? '✓' : '✗'}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
